/**
 * Cloudflare marks challenge responses with `Cf-Mitigated: challenge`. Turbo's fetch
 * then receives challenge HTML instead of the app; a full document load is required.
 *
 * This event’s `detail` only exposes `fetchResponse`, not the original Request, so we
 * cannot read `X-Sec-Purpose` here without pairing every `turbo:before-fetch-request`
 * with a response (fragile under concurrent fetches). Instead we use `event.target`:
 * Turbo dispatches on the `<a>` for link prefetch, and on `<html>` for a normal Drive
 * visit, so we skip `location.assign` for the former and only full-navigate the latter.
 *
 * @see https://developers.cloudflare.com/cloudflare-challenges/challenge-types/challenge-pages/detect-response/
 * @see https://turbo.hotwired.dev/reference/events#http-requests
 */
document.addEventListener('turbo:before-fetch-response', (event: Event) => {
  if (
    !('detail' in event) ||
    !event.detail ||
    typeof event.detail !== 'object'
  ) {
    return;
  }

  const { fetchResponse } = event.detail as {
    fetchResponse: { header: (name: string) => string | null; location: URL };
  };

  if (fetchResponse.header('cf-mitigated') !== 'challenge') {
    return;
  }

  event.preventDefault();

  const isLinkPrefetch = event.target instanceof HTMLAnchorElement;

  if (isLinkPrefetch) {
    return;
  }

  window.location.assign(fetchResponse.location.href);
});

/**
 * This is a workaround to fix the issue where Turbo is not able to find the
 * frame when a page is loaded. This happens quite often since we have 'hero frames'
 * like 'overlay', 'map', and 'modal' that are widely used but not on every page.
 *
 * To get around this, we listen for the `turbo:frame-missing` event and transform
 * the missing frame into a full-page visit. This is the old behaviour of Turbo - the
 * new behaviour treats the missing frame as an error.
 *
 * @see https://github.com/hotwired/turbo/pull/863
 */
document.addEventListener('turbo:frame-missing', ((
  evt: CustomEvent<{
    response: Response;
    visit: (
      location: string | URL | Response,
      visitOptions?: unknown
    ) => Promise<void>;
  }>
) => {
  evt.preventDefault();

  const { visit, response } = evt.detail;

  console.warn(
    `A matching frame for #${(evt.target as HTMLElement).id} was missing from the response, transforming into full-page Visit.`
  );

  visit(response).catch(error => {
    throw error;
  });
}) as EventListener);
